import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosConn from '../../axiosConn';

const DISMISS_KEY = 'jg:feedLocalCircleDismissal:v1';
const DISMISS_MS = 1000 * 60 * 60 * 24 * 7;
const DEFAULT_AVATAR =
  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png';

const canUseStorage = () => typeof window !== 'undefined' && window.localStorage;

const readDismissal = () => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeDismissal = (value) => {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(DISMISS_KEY, JSON.stringify(value));
  } catch {}
};

const getDismissedForSignature = (signature) => {
  const dismissal = readDismissal();
  if (!dismissal?.until || dismissal.until <= Date.now()) return false;
  if (!signature) return true;
  return dismissal.signature === signature;
};

const normalizeSuggestionsPayload = (data) => {
  const payload = data?.data || data || {};
  const suggestions = Array.isArray(payload.suggestions)
    ? payload.suggestions
    : [];
  return {
    localContextUsed: payload.localContextUsed === true,
    signature:
      typeof payload.signature === 'string' ? payload.signature : '',
    suggestions: suggestions.filter((item) => item?._id || item?.id),
  };
};

export const useLocalCircleSuggestions = ({ enabled = true, player, limit = 16 }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [signature, setSignature] = useState('');
  const [localContextUsed, setLocalContextUsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [forcedOpen, setForcedOpen] = useState(false);
  const [requestedIds, setRequestedIds] = useState(() => new Set());
  const [requestingIds, setRequestingIds] = useState(() => new Set());
  const [error, setError] = useState('');

  const token = player?.token;
  const currentPlayerId = player?.data?.player?._id;

  useEffect(() => {
    if (!enabled || !token || !currentPlayerId) {
      setSuggestions([]);
      return undefined;
    }

    const ac = new AbortController();
    (async () => {
      try {
        const res = await axiosConn.get(
          `local-ops/local-circle/suggestions?limit=${limit}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: ac.signal,
          },
        );
        if (ac.signal.aborted) return;
        const payload = normalizeSuggestionsPayload(res?.data);
        setSuggestions(payload.suggestions);
        setSignature(payload.signature);
        setLocalContextUsed(payload.localContextUsed);
        setDismissed(getDismissedForSignature(payload.signature));
        setError('');
      } catch (err) {
        if (ac.signal.aborted) return;
        setSuggestions([]);
        setError('Local Circle is unavailable right now.');
      }
    })();

    return () => ac.abort();
  }, [currentPlayerId, enabled, limit, token]);

  const hasSuggestions = suggestions.length > 0;
  const showExpanded = hasSuggestions && (forcedOpen || !dismissed);

  const dismiss = useCallback(() => {
    writeDismissal({
      signature,
      until: Date.now() + DISMISS_MS,
    });
    setForcedOpen(false);
    setDismissed(true);
  }, [signature]);

  const open = useCallback(() => {
    if (!hasSuggestions) return;
    setDismissed(false);
    setForcedOpen(true);
  }, [hasSuggestions]);

  const sendFriendRequest = useCallback(
    async (targetId) => {
      if (!targetId || !currentPlayerId || !token) return;
      if (requestedIds.has(targetId) || requestingIds.has(targetId)) return;

      setRequestingIds((prev) => new Set(prev).add(targetId));
      try {
        await axiosConn.put(
          `players/addFriend/${targetId}`,
          { id: currentPlayerId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setRequestedIds((prev) => new Set(prev).add(targetId));
      } catch (err) {
        const message =
          err?.response?.data?.message || err?.message || 'Request failed';
        if (/already/i.test(message)) {
          setRequestedIds((prev) => new Set(prev).add(targetId));
        } else {
          setError('Could not send that request. Try again.');
        }
      } finally {
        setRequestingIds((prev) => {
          const next = new Set(prev);
          next.delete(targetId);
          return next;
        });
      }
    },
    [currentPlayerId, requestedIds, requestingIds, token],
  );

  return useMemo(
    () => ({
      dismiss,
      error,
      hasSuggestions,
      localContextUsed,
      open,
      requestedIds,
      requestingIds,
      sendFriendRequest,
      showExpanded,
      signature,
      suggestions,
    }),
    [
      dismiss,
      error,
      hasSuggestions,
      localContextUsed,
      open,
      requestedIds,
      requestingIds,
      sendFriendRequest,
      showExpanded,
      signature,
      suggestions,
    ],
  );
};

const formatActionCredit = (value) => {
  const count = Number(value) || 0;
  if (count <= 0) return 'LocalOps';
  return `${count} Action Credit`;
};

const LocalCircleSuggestions = React.forwardRef(({ state }, ref) => {
  const scrollerRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    hasOverflow: false,
  });
  const suggestionCount = state?.suggestions?.length || 0;
  const showExpanded = state?.showExpanded === true;

  const updateScrollState = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) {
      setScrollState({
        canScrollLeft: false,
        canScrollRight: false,
        hasOverflow: false,
      });
      return;
    }

    const maxScrollLeft = Math.max(0, node.scrollWidth - node.clientWidth);
    const scrollLeft = Math.max(0, node.scrollLeft || 0);
    const hasOverflow = maxScrollLeft > 2;
    setScrollState({
      canScrollLeft: hasOverflow && scrollLeft > 2,
      canScrollRight: hasOverflow && scrollLeft < maxScrollLeft - 2,
      hasOverflow,
    });
  }, []);

  useEffect(() => {
    updateScrollState();
    const node = scrollerRef.current;
    if (!node) return undefined;

    const handleScroll = () => updateScrollState();
    node.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    const timerId = window.setTimeout(updateScrollState, 0);

    return () => {
      node.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      window.clearTimeout(timerId);
    };
  }, [showExpanded, suggestionCount, updateScrollState]);

  const scrollLocals = useCallback(
    (direction) => {
      const node = scrollerRef.current;
      if (!node) return;
      const amount = Math.max(180, Math.floor(node.clientWidth * 0.78));
      node.scrollBy({
        left: direction * amount,
        behavior: 'smooth',
      });
      window.setTimeout(updateScrollState, 260);
    },
    [updateScrollState],
  );

  if (!showExpanded) return null;

  const subtitle = state.localContextUsed
    ? 'Locals building JG LocalOps near you.'
    : 'Locals building JG LocalOps.';

  return (
    <section
      aria-label="Local Circle suggestions"
      className="localCircle"
      ref={ref}
    >
      <div className="localCircleHeader">
        <div>
          <div className="localCircleEyebrow">Local Circle</div>
          <h2>Find locals already here</h2>
          <p>{subtitle}</p>
        </div>
        <button
          aria-label="Dismiss Local Circle"
          className="localCircleDismiss"
          onClick={state.dismiss}
          type="button"
        >
          x
        </button>
      </div>

      <div className="localCircleScrollShell">
        {scrollState.hasOverflow ? (
          <button
            aria-label="Scroll locals left"
            className="localCircleScrollButton localCircleScrollButton--left"
            disabled={!scrollState.canScrollLeft}
            onClick={() => scrollLocals(-1)}
            type="button"
          >
            {'<'}
          </button>
        ) : null}
        <div className="localCircleScroller" ref={scrollerRef}>
          {state.suggestions.map((suggestion) => {
            const id = suggestion._id || suggestion.id;
            const requested = state.requestedIds.has(id);
            const requesting = state.requestingIds.has(id);
            return (
              <article className="localCircleCard" key={id}>
                <Link
                  aria-label={`View ${suggestion.playerId || 'local'} profile`}
                  className="localCircleAvatarLink"
                  to={`/profile/${suggestion.playerId}`}
                >
                  <img
                    alt=""
                    className="localCircleAvatar"
                    src={suggestion.profilePhoto || DEFAULT_AVATAR}
                  />
                </Link>
                <div className="localCircleCardBody">
                  <Link
                    className="localCirclePlayerId"
                    to={`/profile/${suggestion.playerId}`}
                  >
                    {suggestion.playerId}
                  </Link>
                  <div className="localCircleBadge">{suggestion.badge}</div>
                  <div className="localCircleMeta">
                    {formatActionCredit(suggestion.actionCreditCount)}
                  </div>
                </div>
                <button
                  className="localCircleAdd"
                  disabled={requested || requesting}
                  onClick={() => state.sendFriendRequest(id)}
                  type="button"
                >
                  {requested ? 'Requested' : requesting ? 'Sending' : 'Add'}
                </button>
              </article>
            );
          })}
        </div>
        {scrollState.hasOverflow ? (
          <button
            aria-label="Scroll locals right"
            className="localCircleScrollButton localCircleScrollButton--right"
            disabled={!scrollState.canScrollRight}
            onClick={() => scrollLocals(1)}
            type="button"
          >
            {'>'}
          </button>
        ) : null}
      </div>
      {state.error ? <div className="localCircleError">{state.error}</div> : null}
    </section>
  );
});

export default LocalCircleSuggestions;
